import moment from 'moment'
import {
  createInitialProductionOrderItems,
  INITIAL_PRODUCTION_ORDER_REQUEST,
} from './initialState'
import {
  ProductionOrderItem,
  ProductionOrderRequestApiPayload,
  ProductionOrderRequestFormState,
  RESPONSIBILITY_ROW_COUNT,
} from './types'

const parseDate = (value: unknown): Date | null => {
  if (!value) return null
  const m = moment(value as moment.MomentInput)
  return m.isValid() ? m.toDate() : null
}

const toIsoDate = (value: Date | null): string | null => {
  if (!value || !moment(value).isValid()) return null
  return moment(value).toISOString()
}

const parseOptionalInt = (value: string | number | null | undefined): number | null => {
  if (value === '' || value === null || value === undefined) return null
  const n = typeof value === 'number' ? value : parseInt(String(value), 10)
  return Number.isNaN(n) ? null : n
}

const parseOptionalDecimal = (value: string | number | null | undefined): number | null => {
  if (value === '' || value === null || value === undefined) return null
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  return Number.isNaN(n) ? null : n
}

const mapFactoryFromApi = (entry: any) => ({
  enabled: Boolean(entry?.enabled),
  percent: entry?.percent != null ? String(entry.percent) : '',
})

const mapFactoryToApi = (entry: { enabled: boolean; percent: string }) => ({
  enabled: entry.enabled,
  percent: parseOptionalDecimal(entry.percent),
})

const mapResponsibilityFromApi = (rows: any[] | undefined) => {
  const base = Array.from({ length: RESPONSIBILITY_ROW_COUNT }, (_, rowIndex) => ({
    rowIndex,
    owner: null as 'PACO' | 'CUSTOMER' | null,
  }))

  if (!rows?.length) return base

  rows.forEach(row => {
    const idx = Number(row?.rowIndex)
    if (idx >= 0 && idx < RESPONSIBILITY_ROW_COUNT) {
      const owner = row?.owner
      base[idx].owner =
        owner === 'PACO' || owner === 'CUSTOMER' ? owner : null
    }
  })

  return base
}

const toNullableString = (value: string) => (value.trim() === '' ? null : value)

const mapItemsFromApi = (items: any[] | undefined | null): ProductionOrderItem[] => {
  if (!items?.length) return createInitialProductionOrderItems()

  return items.map((row, index) => ({
    id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`,
    no: row?.no ?? '',
    item: row?.item ?? '',
    quantity: row?.quantity != null ? String(row.quantity) : '',
    pricePerQ: row?.pricePerQ != null ? String(row.pricePerQ) : '',
    pilePerUnit: row?.pilePerUnit != null ? String(row.pilePerUnit) : '',
    drivingPerUnit: row?.drivingPerUnit != null ? String(row.drivingPerUnit) : '',
    other: row?.other ?? '',
    pileAndDrivingPerUnit:
      row?.pileAndDrivingPerUnit != null ? String(row.pileAndDrivingPerUnit) : '',
  }))
}

const mapItemsToApi = (items: ProductionOrderItem[]) =>
  items.map((row, index) => ({
    no: toNullableString(row.no) ?? String(index + 1),
    item: toNullableString(row.item),
    quantity: toNullableString(row.quantity),
    pricePerQ: toNullableString(row.pricePerQ),
    pilePerUnit: toNullableString(row.pilePerUnit),
    drivingPerUnit: toNullableString(row.drivingPerUnit),
    other: toNullableString(row.other),
    pileAndDrivingPerUnit: toNullableString(row.pileAndDrivingPerUnit),
  }))

export const apiToForm = (data: any | null): ProductionOrderRequestFormState => {
  if (!data) return { ...INITIAL_PRODUCTION_ORDER_REQUEST }

  const factories = data.productionFactories ?? {}

  return {
    specialPillarEnabled: Boolean(data.specialPillarEnabled),
    specialPillarDate: parseDate(data.specialPillarDate),
    specialStopEnabled: Boolean(data.specialStopEnabled),
    specialStopCount1:
      data.specialStopCount1 != null ? String(data.specialStopCount1) : '',
    specialStopCount2:
      data.specialStopCount2 != null ? String(data.specialStopCount2) : '',
    specialOtherEnabled: Boolean(data.specialOtherEnabled),
    specialOtherDetail: data.specialOtherDetail ?? '',
    items: mapItemsFromApi(data.items),
    productionFactories: {
      factory1: mapFactoryFromApi(factories.factory1),
      factory2: mapFactoryFromApi(factories.factory2),
      factory3: mapFactoryFromApi(factories.factory3),
      joint: mapFactoryFromApi(factories.joint),
    },
    productionCompany: data.productionCompany ?? '',
    documents: {
      soil: Boolean(data.documents?.soil),
      site: Boolean(data.documents?.site),
      contract: Boolean(data.documents?.contract),
      cert: Boolean(data.documents?.cert),
      vat: Boolean(data.documents?.vat),
      other: Boolean(data.documents?.other),
      otherDetail: data.documents?.otherDetail ?? '',
    },
    customerGrade: data.customerGrade ?? '',
    depositPercent: data.depositPercent != null ? String(data.depositPercent) : '',
    depositAmount: data.depositAmount != null ? String(data.depositAmount) : '',
    financeNote: data.financeNote ?? '',
    responsibility: mapResponsibilityFromApi(data.responsibility),
  }
}

export const formToApi = (form: ProductionOrderRequestFormState): ProductionOrderRequestApiPayload => ({
  specialPillarEnabled: form.specialPillarEnabled,
  specialPillarDate: toIsoDate(form.specialPillarDate),
  specialStopEnabled: form.specialStopEnabled,
  specialStopCount1: parseOptionalInt(form.specialStopCount1),
  specialStopCount2: parseOptionalInt(form.specialStopCount2),
  specialOtherEnabled: form.specialOtherEnabled,
  specialOtherDetail: form.specialOtherDetail || null,
  items: mapItemsToApi(form.items),
  productionFactories: {
    factory1: mapFactoryToApi(form.productionFactories.factory1),
    factory2: mapFactoryToApi(form.productionFactories.factory2),
    factory3: mapFactoryToApi(form.productionFactories.factory3),
    joint: mapFactoryToApi(form.productionFactories.joint),
  },
  productionCompany: form.productionCompany || null,
  documents: {
    soil: form.documents.soil,
    site: form.documents.site,
    contract: form.documents.contract,
    cert: form.documents.cert,
    vat: form.documents.vat,
    other: form.documents.other,
    otherDetail: form.documents.otherDetail || null,
  },
  customerGrade: form.customerGrade || null,
  depositPercent: parseOptionalDecimal(form.depositPercent),
  depositAmount: parseOptionalDecimal(form.depositAmount),
  financeNote: form.financeNote || null,
  responsibility: form.responsibility.map(row => ({
    rowIndex: row.rowIndex,
    owner: row.owner,
  })),
})
